import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  productLinesApi, fieldDefinitionsApi,
  BUILTIN_FIELDS,
  type ProductLine, type FieldDefinition,
} from '../../api/productLines';
import { useCompany } from '../../contexts/CompanyContext';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, Check, Search, Layers, Mail } from 'lucide-react';
import Modal from '../Modal';
import EmptyState from '../EmptyState';
import DataTable, { type Column } from '../DataTable';
import { useConfirm } from '../ConfirmDialog';

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text', textarea: 'Textarea', select: 'Dropdown',
  number: 'Number', boolean: 'Checkbox', date: 'Date', url: 'URL',
};

// ── ProductLine editor modal ─────────────────────────────────────────────────
type EditorSection = 'details' | 'builtin' | 'custom';

function ProductLineEditor({
  pl,
  fieldDefinitions,
  saving,
  onSave,
  onClose,
}: {
  pl: ProductLine | null; // null = create new
  fieldDefinitions: FieldDefinition[];
  saving: boolean;
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [section, setSection] = useState<EditorSection>('details');
  const [name, setName] = useState(pl?.name ?? '');
  const [description, setDescription] = useState(pl?.description ?? '');
  const [color, setColor] = useState(pl?.color ?? '#6366f1');
  const [enabledFields, setEnabledFields] = useState<string[]>(
    Array.isArray(pl?.enabledFields) ? (pl!.enabledFields as string[]) : []
  );
  const [emailKeywords, setEmailKeywords] = useState(
    (Array.isArray(pl?.emailKeywords) ? pl!.emailKeywords : []).join(', ')
  );
  const [customFields, setCustomFields] = useState<
    { fieldDefinitionId: string; isRequired: boolean; sortOrder: number }[]
  >(
    (pl?.productLineFields ?? []).map((f, i) => ({
      fieldDefinitionId: f.fieldDefinitionId,
      isRequired: f.isRequired,
      sortOrder: f.sortOrder ?? i,
    }))
  );
  const [fieldSearch, setFieldSearch] = useState('');

  const toggleBuiltin = (key: string) => {
    setEnabledFields(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleCustom = (defId: string) => {
    setCustomFields(prev => {
      const exists = prev.find(f => f.fieldDefinitionId === defId);
      if (exists) return prev.filter(f => f.fieldDefinitionId !== defId);
      return [...prev, { fieldDefinitionId: defId, isRequired: false, sortOrder: prev.length }];
    });
  };

  const toggleRequired = (defId: string) => {
    setCustomFields(prev =>
      prev.map(f => f.fieldDefinitionId === defId ? { ...f, isRequired: !f.isRequired } : f)
    );
  };

  const groups = Array.from(new Set(BUILTIN_FIELDS.map(f => f.group)));

  const visibleDefs = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    if (!q) return fieldDefinitions;
    return fieldDefinitions.filter(d =>
      d.label.toLowerCase().includes(q) ||
      d.slug.toLowerCase().includes(q) ||
      (FIELD_TYPE_LABELS[d.fieldType] ?? d.fieldType).toLowerCase().includes(q)
    );
  }, [fieldDefinitions, fieldSearch]);

  const sections: { id: EditorSection; label: string; count?: number }[] = [
    { id: 'details', label: 'Details' },
    { id: 'builtin', label: 'Built-in Fields', count: enabledFields.length },
    { id: 'custom', label: 'Custom Fields', count: customFields.length },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={pl ? 'Edit Product Line' : 'New Product Line'}
      width={672}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => onSave({
              name,
              description: description || null,
              color,
              enabledFields,
              customFields,
              emailKeywords: emailKeywords.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
            })}
            disabled={!name.trim() || saving}
          >
            {saving ? 'Saving…' : pl ? 'Save changes' : 'Create'}
          </button>
        </>
      }
    >
      {/* Section nav */}
      <div className="flex gap-1 border-b border-line mb-4 -mt-1">
        {sections.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSection(s.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              section === s.id
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {s.label}
            {s.count !== undefined && s.count > 0 && (
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-primary-muted text-primary">{s.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="min-h-[320px] max-h-[60vh] overflow-y-auto pr-1">

        {section === 'details' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end">
              <div>
                <label className="label">Name *</label>
                <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Mobile App" autoFocus />
              </div>
              <div>
                <label className="label">Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" className="w-10 h-10 rounded-lg border border-line cursor-pointer" value={color} onChange={e => setColor(e.target.value)} />
                  <span className="text-sm text-muted font-mono">{color}</span>
                </div>
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <input className="input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" />
            </div>
            <div>
              <label className="label flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Email routing keywords
              </label>
              <textarea
                className="input w-full h-16"
                value={emailKeywords}
                onChange={e => setEmailKeywords(e.target.value)}
                placeholder="e.g. printer, toner, print queue"
              />
              <p className="text-xs text-muted mt-1">
                Comma-separated. When an inbound email's subject or body contains any of these
                (case-insensitive), the ticket is routed to this product line. Use distinctive
                words or phrases.
              </p>
            </div>
          </div>
        )}

        {section === 'builtin' && (
          <div className="space-y-4">
            <p className="text-xs text-muted">
              Choose which standard ticket fields appear when creating tickets for this product line.
            </p>
            {groups.map(group => {
              const groupFields = BUILTIN_FIELDS.filter(f => f.group === group);
              const onCount = groupFields.filter(f => enabledFields.includes(f.key)).length;
              return (
                <div key={group}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                      {group}
                      {onCount > 0 && <span className="ml-2 normal-case font-normal">{onCount} selected</span>}
                    </p>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => {
                        const keys = groupFields.map(f => f.key);
                        const allOn = keys.every(k => enabledFields.includes(k));
                        setEnabledFields(prev => allOn
                          ? prev.filter(k => !keys.includes(k))
                          : Array.from(new Set([...prev, ...keys])));
                      }}
                    >
                      {groupFields.every(f => enabledFields.includes(f.key)) ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {groupFields.map(f => {
                      const on = enabledFields.includes(f.key);
                      return (
                        <button
                          key={f.key}
                          type="button"
                          onClick={() => toggleBuiltin(f.key)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                            on
                              ? 'border-primary bg-primary-muted text-primary'
                              : 'border-line text-muted hover:border-line'
                          }`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                            on ? 'bg-primary border-primary' : 'border-line'
                          }`}>
                            {on && <Check className="w-3 h-3 text-white" />}
                          </span>
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {section === 'custom' && (
          fieldDefinitions.length === 0 ? (
            <EmptyState icon={<Layers className="w-6 h-6" />}>
              No custom fields defined yet. Create them in the <strong>Field Library</strong> first,
              then attach them to product lines here.
            </EmptyState>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                Attach custom fields to this product line and mark which are required.
              </p>
              {fieldDefinitions.length > 6 && (
                <div className="relative">
                  <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input pl-9"
                    value={fieldSearch}
                    onChange={e => setFieldSearch(e.target.value)}
                    placeholder="Search fields…"
                  />
                </div>
              )}
              <div className="space-y-2">
                {visibleDefs.length === 0 && (
                  <p className="text-sm text-muted py-4 text-center">No fields match "{fieldSearch}".</p>
                )}
                {visibleDefs.map(def => {
                  const assignment = customFields.find(f => f.fieldDefinitionId === def.id);
                  const isOn = !!assignment;
                  return (
                    <div
                      key={def.id}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors ${
                        isOn ? 'border-primary bg-primary-muted' : 'border-line'
                      }`}
                    >
                      <button type="button" className="flex items-center gap-2 text-sm flex-1 text-left min-w-0" onClick={() => toggleCustom(def.id)}>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          isOn ? 'bg-primary border-primary' : 'border-line'
                        }`}>
                          {isOn && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="font-medium text-fg truncate">{def.label}</span>
                        <span className="text-xs text-muted shrink-0">({FIELD_TYPE_LABELS[def.fieldType] ?? def.fieldType})</span>
                      </button>
                      {isOn && (
                        <button
                          type="button"
                          onClick={() => toggleRequired(def.id)}
                          className={`ml-3 text-xs px-2 py-1 rounded-md border transition-colors shrink-0 ${
                            assignment?.isRequired
                              ? 'border-err text-err'
                              : 'border-line text-muted'
                          }`}
                        >
                          {assignment?.isRequired ? 'Required' : 'Optional'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>
    </Modal>
  );
}

// ── FieldDefinition create/edit modal ────────────────────────────────────────
function FieldDefinitionEditor({
  def,
  saving,
  onSave,
  onClose,
}: {
  def: FieldDefinition | null;
  saving: boolean;
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(def?.name ?? '');
  const [slug, setSlug] = useState(def?.slug ?? '');
  const [fieldType, setFieldType] = useState<FieldDefinition['fieldType']>(def?.fieldType ?? 'text');
  const [label, setLabel] = useState(def?.label ?? '');
  const [placeholder, setPlaceholder] = useState(def?.placeholder ?? '');
  const [helpText, setHelpText] = useState(def?.helpText ?? '');
  const [isRequired, setIsRequired] = useState(def?.isRequired ?? false);
  const [optionsText, setOptionsText] = useState(
    (def?.options ?? []).map((o: any) => `${o.value}:${o.label}`).join('\n')
  );
  const isEdit = !!def; // slug + type can't change after creation

  const autoSlug = (n: string) => n.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const parseOptions = (text: string) =>
    text.split('\n')
      .map(l => l.trim())
      .filter(Boolean)
      .map(l => {
        const [value, ...rest] = l.split(':');
        return { value: value.trim(), label: (rest.join(':') || value).trim() };
      });

  return (
    <Modal
      open
      onClose={onClose}
      title={def ? 'Edit Custom Field' : 'New Custom Field'}
      width={512}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || !slug.trim() || !label.trim() || saving}
            onClick={() => onSave({
              name, slug, fieldType, label,
              placeholder: placeholder || undefined,
              helpText: helpText || undefined,
              isRequired,
              options: fieldType === 'select' ? parseOptions(optionsText) : [],
            })}
          >
            {saving ? 'Saving…' : def ? 'Save changes' : 'Create field'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Name *</label>
            <input className="input" value={name} autoFocus onChange={e => { setName(e.target.value); if (!isEdit) setSlug(autoSlug(e.target.value)); }} />
          </div>
          <div>
            <label className="label">Slug *</label>
            <input className="input font-mono text-sm" value={slug} onChange={e => setSlug(e.target.value)} disabled={isEdit} />
            {!isEdit && <p className="text-xs text-muted mt-1">Unique key, lowercase + underscores</p>}
          </div>
        </div>
        <div>
          <label className="label">Label * (shown to users)</label>
          <input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. App Store Version" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Field Type</label>
            <select className="select" value={fieldType} onChange={e => setFieldType(e.target.value as any)} disabled={isEdit}>
              {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {isEdit && <p className="text-xs text-muted mt-1">Type can't change after creation</p>}
          </div>
          <div>
            <label className="label">Placeholder</label>
            <input className="input" value={placeholder} onChange={e => setPlaceholder(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Help text</label>
          <input className="input" value={helpText} onChange={e => setHelpText(e.target.value)} placeholder="Brief hint shown below the field" />
        </div>
        {fieldType === 'select' && (
          <div>
            <label className="label">Options (one per line, format: value:Label)</label>
            <textarea className="input font-mono text-xs min-h-[80px] resize-y" value={optionsText} onChange={e => setOptionsText(e.target.value)} placeholder={"ios:iOS\nandroid:Android\nother:Other"} />
          </div>
        )}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 accent-primary" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} />
          <span className="text-sm text-fg">Required by default</span>
        </label>
      </div>
    </Modal>
  );
}

// ── Main tab ─────────────────────────────────────────────────────────────────
export default function ProductLinesTab() {
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [view, setView] = useState<'lines' | 'fields'>('lines');
  const [plEditor, setPlEditor] = useState<ProductLine | 'new' | null>(null);
  const [fieldEditor, setFieldEditor] = useState<FieldDefinition | 'new' | null>(null);
  const [fieldSearch, setFieldSearch] = useState('');

  const { data: productLines = [] } = useQuery({
    queryKey: ['product-lines', companyId],
    queryFn: () => productLinesApi.list(),
    enabled: !!companyId,
  });

  const { data: fieldDefinitions = [] } = useQuery({
    queryKey: ['field-definitions', companyId],
    queryFn: () => fieldDefinitionsApi.list(),
    enabled: !!companyId,
  });

  // ── Product line mutations ──
  const createPLMutation = useMutation({
    mutationFn: async (data: any) => {
      const pl = await productLinesApi.create({
        name: data.name, description: data.description ?? undefined, color: data.color,
        enabledFields: data.enabledFields, emailKeywords: data.emailKeywords,
      });
      if (data.customFields.length > 0) await productLinesApi.setFields(pl.id, data.customFields);
      return pl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      toast.success('Product line created');
      setPlEditor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updatePLMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await productLinesApi.update(id, {
        name: data.name, description: data.description, color: data.color,
        enabledFields: data.enabledFields, emailKeywords: data.emailKeywords,
      });
      await productLinesApi.setFields(id, data.customFields);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      toast.success('Product line updated');
      setPlEditor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletePLMutation = useMutation({
    mutationFn: (id: string) => productLinesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      toast.success('Product line removed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Field definition mutations ──
  const createFieldMutation = useMutation({
    mutationFn: (data: any) => fieldDefinitionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      toast.success('Custom field created');
      setFieldEditor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateFieldMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => fieldDefinitionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      toast.success('Custom field updated');
      setFieldEditor(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteFieldMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) => fieldDefinitionsApi.delete(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['product-lines'] });
      toast.success('Field removed');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSavePL = (data: any) => {
    if (plEditor && plEditor !== 'new') updatePLMutation.mutate({ id: plEditor.id, data });
    else createPLMutation.mutate(data);
  };

  const handleSaveField = (data: any) => {
    if (fieldEditor && fieldEditor !== 'new') {
      // slug and fieldType are immutable after creation
      const { slug: _s, fieldType: _t, ...editable } = data;
      updateFieldMutation.mutate({ id: fieldEditor.id, data: editable });
    } else {
      createFieldMutation.mutate(data);
    }
  };

  const handleDeletePL = async (pl: ProductLine) => {
    if (await confirm({ title: 'Remove product line', message: `Remove "${pl.name}"?`, confirmLabel: 'Remove', danger: true })) {
      deletePLMutation.mutate(pl.id);
    }
  };

  const handleDeleteField = async (def: FieldDefinition) => {
    const used = def._count?.productLineFields ?? 0;
    const message = used > 0
      ? `"${def.label}" is attached to ${used} product line${used === 1 ? '' : 's'}. Deleting it will remove it from ${used === 1 ? 'that product line' : 'those product lines'} too.`
      : `Delete field "${def.label}"?`;
    if (await confirm({ title: 'Delete custom field', message, confirmLabel: 'Delete', danger: true })) {
      deleteFieldMutation.mutate({ id: def.id, force: used > 0 });
    }
  };

  // ── Field library search ──
  const visibleFieldDefs = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    const defs = fieldDefinitions as FieldDefinition[];
    if (!q) return defs;
    return defs.filter(d =>
      d.label.toLowerCase().includes(q) ||
      d.slug.toLowerCase().includes(q) ||
      (FIELD_TYPE_LABELS[d.fieldType] ?? d.fieldType).toLowerCase().includes(q)
    );
  }, [fieldDefinitions, fieldSearch]);

  // ── Table columns ──
  const plColumns: Column<ProductLine>[] = [
    {
      key: 'name',
      header: 'Product line',
      render: pl => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: pl.color }} />
          <div className="min-w-0">
            <span className="font-medium text-fg">{pl.name}</span>
            {pl.description && <p className="text-xs text-muted truncate max-w-[320px]">{pl.description}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'fields',
      header: 'Ticket fields',
      render: pl => {
        const builtin = (pl.enabledFields as string[])?.length ?? 0;
        const custom = pl.productLineFields?.length ?? 0;
        if (!builtin && !custom) return <span className="text-xs text-muted">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {builtin > 0 && <span className="badge bg-row-odd text-muted text-xs">{builtin} built-in</span>}
            {custom > 0 && <span className="badge bg-primary-muted text-primary text-xs">{custom} custom</span>}
          </div>
        );
      },
    },
    {
      key: 'keywords',
      header: 'Email routing',
      render: pl => {
        const n = (pl.emailKeywords as string[])?.length ?? 0;
        return n > 0
          ? <span className="text-xs text-fg inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-muted" />{n} keyword{n === 1 ? '' : 's'}</span>
          : <span className="text-xs text-muted">—</span>;
      },
    },
    {
      key: 'tickets',
      header: 'Tickets',
      cellStyle: { width: 80 },
      render: pl => <span className="text-sm text-muted">{pl._count?.tickets ?? 0}</span>,
    },
    {
      key: 'actions',
      header: '',
      cellStyle: { width: 90 },
      render: pl => (
        <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
          <button className="p-1.5 text-muted hover:text-primary rounded transition-colors" title="Edit" onClick={() => setPlEditor(pl)}>
            <Pencil className="w-4 h-4" />
          </button>
          <button className="p-1.5 text-muted hover:text-err rounded transition-colors" title="Delete" onClick={() => handleDeletePL(pl)}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const fieldColumns: Column<FieldDefinition>[] = [
    {
      key: 'label',
      header: 'Field',
      render: def => (
        <div className="min-w-0">
          <span className="font-medium text-fg">{def.label}</span>
          {def.helpText && <p className="text-xs text-muted truncate max-w-[280px]">{def.helpText}</p>}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cellStyle: { width: 110 },
      render: def => (
        <span className="text-xs font-mono text-muted bg-row-odd px-2 py-0.5 rounded">
          {FIELD_TYPE_LABELS[def.fieldType] ?? def.fieldType}
        </span>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      render: def => <span className="text-xs font-mono text-muted">{def.slug}</span>,
    },
    {
      key: 'required',
      header: 'Default',
      cellStyle: { width: 100 },
      render: def => def.isRequired
        ? <span className="badge text-xs text-err">Required</span>
        : <span className="text-xs text-muted">Optional</span>,
    },
    {
      key: 'usage',
      header: 'Used in',
      cellStyle: { width: 120 },
      render: def => {
        const n = def._count?.productLineFields ?? 0;
        return n > 0
          ? <span className="text-xs text-fg">{n} line{n === 1 ? '' : 's'}</span>
          : <span className="text-xs text-muted">Not used</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      cellStyle: { width: 90 },
      render: def => (
        <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
          <button className="p-1.5 text-muted hover:text-primary rounded transition-colors" title="Edit" onClick={() => setFieldEditor(def)}>
            <Pencil className="w-4 h-4" />
          </button>
          <button className="p-1.5 text-muted hover:text-err rounded transition-colors" title="Delete" onClick={() => handleDeleteField(def)}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const lines = productLines as ProductLine[];
  const defs = fieldDefinitions as FieldDefinition[];

  return (
    <div className="space-y-4">
      {/* View switcher + primary action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-line p-0.5">
          <button
            className={`px-3.5 py-1.5 text-sm rounded-md transition-colors ${
              view === 'lines' ? 'bg-primary-muted text-primary font-medium' : 'text-muted hover:text-fg'
            }`}
            onClick={() => setView('lines')}
          >
            Product Lines
            <span className="ml-1.5 text-xs opacity-70">{lines.length}</span>
          </button>
          <button
            className={`px-3.5 py-1.5 text-sm rounded-md transition-colors ${
              view === 'fields' ? 'bg-primary-muted text-primary font-medium' : 'text-muted hover:text-fg'
            }`}
            onClick={() => setView('fields')}
          >
            Field Library
            <span className="ml-1.5 text-xs opacity-70">{defs.length}</span>
          </button>
        </div>

        {view === 'lines' ? (
          <button className="btn btn-primary inline-flex items-center gap-2" onClick={() => setPlEditor('new')}>
            <Plus className="w-4 h-4" />New product line
          </button>
        ) : (
          <button className="btn btn-primary inline-flex items-center gap-2" onClick={() => setFieldEditor('new')}>
            <Plus className="w-4 h-4" />New field
          </button>
        )}
      </div>

      {view === 'lines' && (
        <>
          <p className="text-sm text-muted">
            Each product line controls which fields appear when creating tickets, and can route
            inbound email by keyword. Click a row to edit.
          </p>
          <DataTable
            columns={plColumns}
            rows={lines}
            rowKey={pl => pl.id}
            onRowClick={pl => setPlEditor(pl)}
            empty={
              <span>
                No product lines yet. Click <strong>New product line</strong> to customize ticket
                fields per product.
              </span>
            }
          />
        </>
      )}

      {view === 'fields' && (
        <>
          <p className="text-sm text-muted">
            Reusable custom fields. Define a field once here, then attach it to any product line —
            required or optional per line.
          </p>
          {defs.length > 5 && (
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="input pl-9"
                value={fieldSearch}
                onChange={e => setFieldSearch(e.target.value)}
                placeholder="Search by label, slug, or type…"
              />
            </div>
          )}
          <DataTable
            columns={fieldColumns}
            rows={visibleFieldDefs}
            rowKey={def => def.id}
            onRowClick={def => setFieldEditor(def)}
            empty={
              fieldSearch.trim()
                ? <span>No fields match "{fieldSearch}".</span>
                : <span>No custom fields yet. Click <strong>New field</strong> to define one for use in product lines.</span>
            }
          />
        </>
      )}

      {/* Modals */}
      {plEditor !== null && (
        <ProductLineEditor
          pl={plEditor === 'new' ? null : plEditor}
          fieldDefinitions={defs}
          saving={createPLMutation.isPending || updatePLMutation.isPending}
          onSave={handleSavePL}
          onClose={() => setPlEditor(null)}
        />
      )}

      {fieldEditor !== null && (
        <FieldDefinitionEditor
          def={fieldEditor === 'new' ? null : fieldEditor}
          saving={createFieldMutation.isPending || updateFieldMutation.isPending}
          onSave={handleSaveField}
          onClose={() => setFieldEditor(null)}
        />
      )}
    </div>
  );
}
